import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, Phone } from "lucide-react";

const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navLinkClass = (path) => {
    const base = "font-medium transition-colors";
    return isActive(path)
      ? `${base} text-blue-600`
      : `${base} text-gray-700 hover:text-gray-900`;
  };

  return (
    <header
      data-testid="header"
      className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-md" : ""
      }`}
      style={{ height: "70px" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo"
          className="text-2xl font-bold"
          style={{ color: "#2C5F2D" }}
        >
          A3 Technologies Inc.
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          <Link
            to="/"
            className={navLinkClass("/")}
            data-testid="nav-home"
          >
            Home
          </Link>
          <Link
            to="/services"
            className={navLinkClass("/services")}
            data-testid="nav-services"
          >
            Services
          </Link>
          <Link
            to="/about"
            className={navLinkClass("/about")}
            data-testid="nav-about"
          >
            About
          </Link>
          <Link
            to="/contact"
            className={navLinkClass("/contact")}
            data-testid="nav-contact"
          >
            Contact
          </Link>
          <a
            href="tel:7783497449"
            className="flex items-center gap-2 font-medium transition-colors"
            style={{ color: "#3B82F6" }}
            data-testid="nav-phone"
          >
            <Phone size={18} />
            (778) 349-7449
          </a>
        </nav>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-white">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">Main navigation menu for A3 Technologies</SheetDescription>
              <div className="flex flex-col h-full pt-8">
                <a
                  href="tel:7783497449"
                  className="flex items-center gap-2 text-lg font-semibold mb-8 pb-4 border-b"
                  style={{ color: "#3B82F6" }}
                  data-testid="mobile-phone"
                >
                  <Phone size={20} />
                  (778) 349-7449
                </a>
                <nav className="flex flex-col gap-6">
                  <SheetClose asChild>
                    <Link
                      to="/"
                      className={`text-xl py-2 ${isActive("/") ? "text-blue-600 font-semibold" : "text-gray-800 hover:text-gray-600"}`}
                      data-testid="mobile-nav-home"
                    >
                      Home
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/services"
                      className={`text-xl py-2 ${isActive("/services") ? "text-blue-600 font-semibold" : "text-gray-800 hover:text-gray-600"}`}
                      data-testid="mobile-nav-services"
                    >
                      Services
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/about"
                      className={`text-xl py-2 ${isActive("/about") ? "text-blue-600 font-semibold" : "text-gray-800 hover:text-gray-600"}`}
                      data-testid="mobile-nav-about"
                    >
                      About
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/contact"
                      className={`text-xl py-2 ${isActive("/contact") ? "text-blue-600 font-semibold" : "text-gray-800 hover:text-gray-600"}`}
                      data-testid="mobile-nav-contact"
                    >
                      Contact
                    </Link>
                  </SheetClose>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
